import React from 'react';
import { ChevronRight } from 'lucide-react';
import { RequestStatusBadge } from '../../components/RequestBadges';

const RISK_RIBBON = {
  high: 'var(--color-red-600)',
  medium: 'var(--color-yellow-500)',
  low: 'var(--color-green-500)',
};

export function ribbonColor(rating) {
  return RISK_RIBBON[String(rating || '').toLowerCase()] || 'var(--border-primary)';
}

export function TimelineItem({ history, isLast, formatDateTime }) {
  const dotColor = {
    draft: 'var(--color-gray-400)',
    submitted: 'var(--color-blue-500)',
    under_review: 'var(--color-yellow-500)',
    approved: 'var(--color-green-500)',
    rejected: 'var(--color-red-500)',
    withdrawn: 'var(--color-gray-400)',
  }[history.newStatus] || 'var(--color-gray-400)';

  return (
    <div style={{ position: 'relative', display: 'flex', gap: 12 }}>
      {!isLast && (
        <div
          style={{
            position: 'absolute', left: 7, top: 18, bottom: 0,
            width: 0.5, background: 'var(--border-primary)',
          }}
        />
      )}
      <div
        style={{
          width: 14, height: 14, borderRadius: 999,
          background: dotColor,
          flexShrink: 0, marginTop: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{ width: 4, height: 4, borderRadius: 999, background: '#fff' }} />
      </div>
      <div style={{ flex: 1, paddingBottom: 16, minWidth: 0 }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {history.oldStatus ? (
            <>
              <RequestStatusBadge status={history.oldStatus} size="small" />
              <ChevronRight className="w-3 h-3" style={{ color: 'var(--icon-tertiary)' }} />
              <RequestStatusBadge status={history.newStatus} size="small" />
            </>
          ) : (
            <>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Created as</span>
              <RequestStatusBadge status={history.newStatus} size="small" />
            </>
          )}
        </div>
        {history.note && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.45 }}>
            {history.note}
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
          {formatDateTime(history.createdAt)}
          {history.changedByName && <span> · {history.changedByName}</span>}
        </div>
      </div>
    </div>
  );
}

export function Subhead({ children }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        color: 'var(--text-tertiary)',
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

export function LongText({ children }) {
  return (
    <div
      style={{
        fontSize: 13,
        lineHeight: 1.55,
        whiteSpace: 'pre-wrap',
        color: 'var(--text-primary)',
      }}
    >
      {children}
    </div>
  );
}

export function FlagList({ items }) {
  const flags = Array.isArray(items) ? items : [];
  if (flags.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
        No flags identified.
      </div>
    );
  }
  return (
    <div>
      {flags.map((flag, index) => {
        const severity = String(flag.severity || 'medium').toLowerCase();
        const tone = severity === 'high' ? 'red' : severity === 'low' ? 'gray' : 'yellow';
        return (
          <div
            key={`${flag.title || 'flag'}-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '70px 1fr',
              gap: 14,
              padding: '12px 0',
              alignItems: 'flex-start',
              borderBottom:
                index === flags.length - 1
                  ? 0
                  : '0.5px solid var(--border-primary)',
            }}
          >
            <div>
              <span
                className={`chip ${tone} sq`}
                style={{ textTransform: 'capitalize' }}
              >
                {severity}
              </span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 450,
                  color: 'var(--text-primary)',
                }}
              >
                {flag.title || 'Review flag'}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  marginTop: 4,
                }}
              >
                {flag.detail || 'No detail provided.'}
              </div>
              {flag.policyReference && (
                <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                  {flag.policyReference}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TextList({ items, empty, tone = 'default' }) {
  const values = Array.isArray(items) ? items.filter(Boolean) : [];
  if (values.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
        {empty}
      </div>
    );
  }

  return (
    <div className="stack-sm">
      {values.map((item, index) => (
        <div
          key={`${item}-${index}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '18px 1fr',
            gap: 8,
            alignItems: 'start',
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--text-secondary)',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: tone === 'followup' ? 'var(--color-yellow-500)' : 'var(--color-blue-500)',
              marginTop: 7,
              justifySelf: 'center',
            }}
          />
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}
